import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Check } from '../check/Check'
import { SubmitCheckFormValues, SubmitCheckResult } from '../submitCheckModal/SubmitCheckModal.types'
import { SubmitCheckModal } from '../submitCheckModal/SubmitCheckModal'
import { useCreateSubmitCheckHelpers } from '../../hooks/useCreateSubmitCheckHelpers'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useMutation, useQuery } from '@percent/utility'
import { ProfileDataCheckContainerProps } from './ProfileDataCheckContainer.types'
import {
  CheckType,
  MonetaryVerificationCheck
} from '@percent/admin-dashboard/api/actions/monetaryVerifications/monetaryVerifications.types'

export function ProfileDataCheckContainer({
  monetaryVerification,
  organisationName,
  partnerName,
  validationSubmission
}: Readonly<ProfileDataCheckContainerProps>) {
  const { monetaryVerificationsService } = useServices()
  const { t } = useTranslation()

  const [checkToSubmit, setCheckToSubmit] = useState<MonetaryVerificationCheck | null>(null)
  const [submitCheckStatus, setSubmitCheckStatus] = useState<SubmitCheckResult | null>(null)

  const { data, isLoading, errorMessage } = useQuery(
    ['v1/admin/experience/profile-data-checks', monetaryVerification.id],
    monetaryVerificationsService.getProfileDataCheck,
    { verificationId: monetaryVerification.id },
    { enabled: !!monetaryVerification.id }
  )

  const { createRequestPayload, invalidateQueryKeys } = useCreateSubmitCheckHelpers(
    monetaryVerification,
    data?.status,
    validationSubmission
  )

  const [{ isPending }, { apiFunc: submitProfileDataCheck }] = useMutation(
    monetaryVerificationsService.submitProfileDataCheck,
    () => {
      setSubmitCheckStatus(SubmitCheckResult.SUCCESS)
    },
    () => setSubmitCheckStatus(SubmitCheckResult.ERROR)
  )

  const handleSubmit = (formData: SubmitCheckFormValues) => {
    submitProfileDataCheck(createRequestPayload(formData))
  }

  const handleCloseModal = () => {
    setCheckToSubmit(null)
    setSubmitCheckStatus(null)

    if (submitCheckStatus === SubmitCheckResult.SUCCESS) {
      invalidateQueryKeys(['v1/admin/experience/profile-data-checks', monetaryVerification?.id])
    }
  }

  const checkTitle = t('typography.profileDataCheck')

  return (
    <>
      <Check
        check={data ? { ...data, type: CheckType.PROFILE_DATA_CHECK } : undefined}
        monetaryVerification={monetaryVerification}
        onCheckToSubmit={setCheckToSubmit}
        title={checkTitle}
        isLoading={isLoading}
        isError={!!errorMessage}
      />
      {checkToSubmit && data && (
        <SubmitCheckModal
          isOpen={!!checkToSubmit}
          onClose={handleCloseModal}
          partnerName={partnerName}
          organisationName={organisationName}
          onSubmit={handleSubmit}
          isPending={isPending}
          submitCheckStatus={submitCheckStatus}
          checkType={CheckType.PROFILE_DATA_CHECK}
          checkStatus={data?.status}
          checkTitle={checkTitle}
        />
      )}
    </>
  )
}
